import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useErrorHandler, APIError, ClientError, useQueryClient, useJobStatus, JobIdResponse } from 'shared-ui';
import { toast } from 'react-toastify';

import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { addGroupTerminalPermissions } from 'api/permissions/permissions';
import { fetchServers } from 'api/servers/servers';
import { fetchTerminals } from 'api/terminals/terminals';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useMutation } from 'hooks/useMutation/useMutation';
import { GroupTerminalsPermissionsIdSortEnum } from 'context/query/queryContext/QueryContext.enum';
import { DEFAULT_PERMISSIONS } from 'core/global.variables';
import { useResetPermissionsFormValues } from 'hooks/useResetPermissionsFormValues/useResetPermissionsFormValues';
import { useModalState } from 'hooks/useModalState/useModalState';

import { useAddGroupTerminalPermissionValidation } from './AddGroupTerminalPermission.validation';
import {
  AddGroupTerminalPermissionFormBody,
  AddGroupTerminalPermissionContainerProps,
} from './AddGroupTerminalPermission.types';
import { AddGroupTerminalPermission } from './AddGroupTerminalPermission';

const isJobResponse = (response: JobIdResponse | void): response is JobIdResponse => {
  return response !== undefined && response !== null && typeof response === 'object' && 'jobId' in response;
};

export const AddGroupTerminalPermissionContainer = ({ groupId }: AddGroupTerminalPermissionContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const [selectedServerId, setSelectedServerId] = useState('');
  const [terminalsQuery] = useState('');
  const { schema } = useAddGroupTerminalPermissionValidation();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();
  const { handleError } = useErrorHandler();

  const jobStatus = useJobStatus({
    autoClose: false,
    useGlobalModal: true,
    showItemCount: false,
    onComplete: () => {
      queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS_SERVERS]);
      queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS]);
      toast.dark(formatMessage({ id: AppMessages['group.reader.permissions.addPermission.success'] }));
    },
    customCompleteMessage: formatMessage({ id: AppMessages['groups.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['groups.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['groups.result.waiting'] }),
    title: formatMessage({ id: AppMessages['groups.result.title'] }),
  });

  const methods = useForm<AddGroupTerminalPermissionFormBody>({
    defaultValues: {
      ...DEFAULT_PERMISSIONS,
      idTkds: [],
    },
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const { data: serversResponse, isLoading: isLoadingServers } = useQuery(
    [QueryKeyEnum.SERVERS, groupId],
    () => fetchServers(groupId),
    {
      onSuccess: (serversResponse) => {
        if (serversResponse.data.length) {
          setSelectedServerId(serversResponse.data[0].id);
        }
      },
    },
  );

  const { data: terminalsResponse, isInitialLoading: isLoadingTerminals } = useQuery(
    [QueryKeyEnum.TERMINALS, selectedServerId, terminalsQuery],
    () => fetchTerminals(selectedServerId),
    {
      args: {
        zaakceptowane: true,
        sort: [GroupTerminalsPermissionsIdSortEnum.ASC],
        withoutGlc: true,
      },
      enabled: !!selectedServerId,
    },
  );

  const { mutate: onAddGroupTerminalPermissions, isLoading } = useMutation(addGroupTerminalPermissions, {
    onSuccess: (response) => {
      if (isJobResponse(response)) {
        jobStatus.startJob(response.jobId);
        onModalClose();
      } else {
        queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS_SERVERS]);
        queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS]);
        toast.dark(formatMessage({ id: AppMessages['group.reader.permissions.addPermission.success'] }));
        onModalClose();
      }
    },
    onError: (error: ClientError<APIError>) => {
      handleError({ error });
    },
  });

  const onSubmit = (body: AddGroupTerminalPermissionFormBody) => {
    onAddGroupTerminalPermissions({
      id: groupId,
      body: {
        ...body,
        idTkds: body.idTkds.map(({ value }) => `${value}`),
      },
    });
  };

  useResetPermissionsFormValues({ selectedValues: methods.watch('idTkds'), setFormValue: methods.setValue });

  useEffect(() => {
    methods.reset();
  }, [isModalOpen, selectedServerId]);

  return (
    <FormProvider {...methods}>
      <AddGroupTerminalPermission
        onSubmit={onSubmit}
        isModalOpen={isModalOpen}
        onModalOpen={onModalOpen}
        onModalClose={onModalClose}
        isLoading={isLoading}
        servers={serversResponse?.data || []}
        selectedServerId={selectedServerId}
        setSelectedServerId={setSelectedServerId}
        isLoadingTerminals={isLoadingTerminals}
        terminals={terminalsResponse?.data || []}
        isLoadingServers={isLoadingServers}
      />
    </FormProvider>
  );
};
